import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import api, { fileUrl } from "@/lib/api";
import { Megaphone, FileText, CalendarClock, BookOpen, ExternalLink, Link as LinkIcon } from "lucide-react";
import Marketplace from "@/components/Marketplace";

const KIND_META = {
  printable: { label: "Printable", cls: "bg-terracotta-light text-terracotta-dark", icon: FileText },
  schedule: { label: "Schedule", cls: "bg-sage-light text-sage-dark", icon: CalendarClock },
  guide: { label: "Guide", cls: "bg-amber-100 text-amber-800", icon: BookOpen },
  link: { label: "Link", cls: "bg-sky-100 text-sky-800", icon: LinkIcon },
};

export default function Resources() {
  const [announcements, setAnnouncements] = useState([]);
  const [resources, setResources] = useState([]);
  const location = useLocation();
  const initialTab = new URLSearchParams(location.search).get("tab") === "directory" ? "directory" : "noticeboard";
  const [tab, setTab] = useState(initialTab);

  useEffect(() => {
    const t = new URLSearchParams(location.search).get("tab");
    setTab(t === "directory" ? "directory" : "noticeboard");
  }, [location.search]);

  useEffect(() => {
    api.get("/announcements").then((r) => setAnnouncements(r.data)).catch(() => {});
    api.get("/resources").then((r) => setResources(r.data)).catch(() => {});
  }, []);

  // Marketplace nav link lands here with ?tab=directory. Render the Marketplace
  // standalone — with its own header — so the user sees only Marketplace
  // listings and never the Resource Hub announcements/materials.
  if (tab === "directory") {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="marketplace-page">
        <div className="mb-8">
          <p className="text-sm text-stone-600 font-semibold tracking-wide uppercase">Community marketplace</p>
          <h1 className="text-3xl sm:text-4xl font-black text-forest mt-1">Marketplace</h1>
          <p className="text-forest/60 mt-1">Physical goods from local families — books, craft supplies, kit, and more.</p>
        </div>
        <Marketplace />
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-10" data-testid="resources-page">
      <div className="mb-8">
        <p className="text-sm text-stone-600 font-semibold tracking-wide uppercase">Community noticeboard</p>
        <h1 className="text-3xl sm:text-4xl font-black text-forest mt-1">Resource hub</h1>
        <p className="text-forest/60 mt-1">Announcements, printables, schedules, and the community marketplace — all in one place.</p>
      </div>

      {tab === "noticeboard" && (<>
      <section className="mb-10">
        <h2 className="text-xl font-black text-forest mb-4 flex items-center gap-2">
          <Megaphone className="w-5 h-5 text-terracotta-dark" /> Announcements
        </h2>
        {announcements.length === 0 ? (
          <div className="bg-white/60 border border-sage/20 rounded-3xl p-8 text-center text-forest/60" data-testid="no-announcements">
            No announcements right now.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 gap-4">
            {announcements.map((a, i) => (
              <div key={a.announcement_id}
                className={`fade-in-${Math.min(i % 4, 3)} bg-white/80 border rounded-3xl p-6 shadow-sm ${a.pinned ? "border-terracotta/40" : "border-sage/20"}`}
                data-testid={`announcement-${a.announcement_id}`}>
                <div className="flex items-center gap-2 mb-2">
                  {a.pinned && <span className="text-[10px] font-bold uppercase tracking-wide bg-terracotta-light text-terracotta-dark px-2 py-0.5 rounded-full">Pinned</span>}
                  <span className="text-xs text-forest/40 ml-auto">{new Date(a.created_at).toLocaleDateString()}</span>
                </div>
                <h3 className="font-heading font-bold text-forest">{a.title}</h3>
                <p className="text-sm text-forest/70 mt-1 leading-relaxed whitespace-pre-line">{a.body}</p>
              </div>
            ))}
          </div>
        )}
      </section>

      <section>
        <h2 className="text-xl font-black text-forest mb-4 flex items-center gap-2">
          <FileText className="w-5 h-5 text-sage-dark" /> Materials & schedules
        </h2>
        {resources.length === 0 ? (
          <div className="bg-white/60 border border-sage/20 rounded-3xl p-8 text-center text-forest/60" data-testid="no-resources">
            No resources shared yet.
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {resources.map((r, i) => {
              const meta = KIND_META[r.kind] || KIND_META.guide;
              const isLink = r.kind === "link" && r.url;
              const href = isLink ? r.url : (r.file_path ? fileUrl(r.file_path) : null);
              const body = (
                <>
                  <div className="flex items-center justify-between mb-3">
                    <span className={`inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide px-2.5 py-1 rounded-full ${meta.cls}`}>
                      <meta.icon className="w-3.5 h-3.5" /> {meta.label}
                    </span>
                    {href && <ExternalLink className="w-4 h-4 text-forest/30" />}
                  </div>
                  <h3 className="font-heading font-bold text-forest">{r.title}</h3>
                  {r.description && <p className="text-sm text-forest/60 mt-1 leading-relaxed">{r.description}</p>}
                  {r.filename && <p className="text-xs text-forest/40 mt-2 flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> {r.filename}</p>}
                  {isLink && <p className="text-xs text-sky-700 mt-2 flex items-center gap-1 truncate"><LinkIcon className="w-3.5 h-3.5 shrink-0" /> <span className="truncate">{r.url}</span></p>}
                </>
              );
              const cls = `fade-in-${Math.min(i % 4, 3)} bg-white/80 border border-sage/20 rounded-3xl p-6 shadow-sm hover:shadow-md transition-shadow block`;
              return href ? (
                <a key={r.resource_id} href={href} target="_blank" rel="noopener noreferrer" className={cls} data-testid={`resource-${r.resource_id}`}>{body}</a>
              ) : (
                <div key={r.resource_id} className={cls} data-testid={`resource-${r.resource_id}`}>{body}</div>
              );
            })}
          </div>
        )}
      </section>
      </>)}
    </div>
  );
}
